var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { getBrowserName } from '@cdo/apps/util/browser-detector';
import { isDevelopmentEnvironment } from '@cdo/apps/utils';
import DashboardMetricsApi from './DashboardMetricsApi';
/**
 * If we receive an unauthorized response from the server, this may
 * indicate that browser event reporting has been temporarily disabled.
 * We will wait for a specific time interval (defined below) before making
 * a request again, so as to not flood the server with requests.
 */
var CHECK_CAN_REPORT_INTERVAL_MINUTES = 30;
var CHECK_CAN_REPORT_INTERVAL_MS = CHECK_CAN_REPORT_INTERVAL_MINUTES * 60 * 1000;
var LOCAL_STORAGE_KEY_NAME = 'cdo-metrics-reporter-last-check-time';
// A flag that can be toggled to send events regardless of environment
var ALWAYS_SEND = false;
/**
 * Reports logs and metrics, intended primarily for developer-facing
 * error reporting, metric reporting, and logging.
 *
 * For tracking user interactions and behaviors (product-facing),
 * see {@link AnalyticsReporter} which reports to Amplitude.
 *
 * For legacy client-side reporting see {@link firehose} for AWS
 * Firehose reporting and {@link logToCloud} for New Relic reporting.
 */
var MetricsReporter = /** @class */ (function () {
    function MetricsReporter(metricsApi) {
        this.metricsApi = metricsApi;
        this.metricsApi = metricsApi;
        this.lastCheckCanReportTime =
            parseInt(localStorage.getItem(LOCAL_STORAGE_KEY_NAME) || '0') || 0;
    }
    /**
     * Publish an information log message. Can be a string or a structured object
     */
    MetricsReporter.prototype.logInfo = function (message) {
        if (!this.shouldReport()) {
            console.log(message);
            return;
        }
        this.log('INFO', message);
    };
    /**
     * Publish a warning log message. Can be a string or a structured object
     */
    MetricsReporter.prototype.logWarning = function (message) {
        if (!this.shouldReport()) {
            console.warn(message);
            return;
        }
        this.log('WARNING', message);
    };
    /**
     * Publish an error log message. Can be a string or a structured object
     */
    MetricsReporter.prototype.logError = function (message) {
        if (!this.shouldReport()) {
            console.error(message);
            return;
        }
        this.log('SEVERE', message);
    };
    /**
     * Increment a counter metric.
     */
    MetricsReporter.prototype.incrementCounter = function (name, dimensions) {
        if (dimensions === void 0) { dimensions = []; }
        this.publishMetric(name, 1, 'Count', dimensions);
    };
    /**
     * Publish a metric.
     */
    MetricsReporter.prototype.publishMetric = function (name, value, unit, dimensions) {
        if (dimensions === void 0) { dimensions = []; }
        var metric = {
            name: name,
            value: value,
            unit: unit,
            dimensions: dimensions.concat(this.getDeviceDimensions()),
        };
        if (!this.shouldReport()) {
            console.info('[MetricsReporter] ' + JSON.stringify(metric));
            return;
        }
        this.sendMetric(metric);
    };
    MetricsReporter.prototype.log = function (level, message) {
        return __awaiter(this, void 0, void 0, function () {
            var payload, error_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        payload = {
                            level: level,
                            message: message,
                            deviceInfo: this.getDeviceInfo(),
                        };
                        if (!this.isReportingEnabled()) {
                            this.fallbackLog(payload);
                            return [2 /*return*/];
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this.metricsApi.sendLogs([payload])];
                    case 2:
                        _a.sent();
                        return [3 /*break*/, 4];
                    case 3:
                        error_1 = _a.sent();
                        this.fallbackLog(payload);
                        this.handleError(error_1);
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    MetricsReporter.prototype.sendMetric = function (metric) {
        return __awaiter(this, void 0, void 0, function () {
            var error_2;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!this.isReportingEnabled()) {
                            this.fallbackLog(metric);
                            return [2 /*return*/];
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this.metricsApi.sendMetricData([metric])];
                    case 2:
                        _a.sent();
                        return [3 /*break*/, 4];
                    case 3:
                        error_2 = _a.sent();
                        this.fallbackLog(metric);
                        this.handleError(error_2);
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    MetricsReporter.prototype.handleError = function (error) {
        if (error.message.includes('401')) {
            // Unauthorized response from server; client logging is likely disabled.
            // We will check again after a time period of CHECK_CAN_REPORT_INTERVAL
            this.setReportingDisabled();
        }
        else {
            console.error(error);
        }
    };
    MetricsReporter.prototype.getDeviceInfo = function () {
        return {
            user_agent: window.navigator.userAgent,
            window_width: window.innerWidth,
            window_height: window.innerHeight,
            hostname: window.location.hostname,
            full_path: window.location.href,
        };
    };
    MetricsReporter.prototype.getDeviceDimensions = function () {
        return [
            {
                name: 'Hostname',
                value: window.location.hostname,
            },
            {
                name: 'Browser',
                value: getBrowserName(),
            },
            {
                name: 'BrowserVersion',
                value: getBrowserName(true),
            },
        ];
    };
    MetricsReporter.prototype.fallbackLog = function (payload) {
        if (isDevelopmentEnvironment()) {
            console.log('Client-side reporting disabled. Attempted to report: ' +
                JSON.stringify(payload));
        }
    };
    MetricsReporter.prototype.isReportingEnabled = function () {
        return (Date.now() - this.lastCheckCanReportTime > CHECK_CAN_REPORT_INTERVAL_MS);
    };
    MetricsReporter.prototype.setReportingDisabled = function () {
        this.lastCheckCanReportTime = Date.now();
        localStorage.setItem(LOCAL_STORAGE_KEY_NAME, this.lastCheckCanReportTime.toString());
    };
    /**
     * Whether we should try to report metrics to the server.
     * Always true if not on development. If on development,
     * this is controlled by the ALWAYS_SEND flag.
     */
    MetricsReporter.prototype.shouldReport = function () {
        return ALWAYS_SEND || !isDevelopmentEnvironment();
    };
    return MetricsReporter;
}());
export default new MetricsReporter(new DashboardMetricsApi());
